import React, { useEffect, useRef } from 'react'
import { skipToken } from '@reduxjs/toolkit/query/react'

import { useAuthState } from '../contexts/authentication-context'
import { useRxpEligibility } from '../contexts/RxpEligibilityContext'
import { useIsEmbed } from '../hooks'
import { useReduxSelector } from '../hooks/use-redux-selector'
import { useRxpExperimentSetup } from '../hooks/use-rxp-experiment-setup'
import { useGetRxpEligibilityQuery } from '../modules/flexApi'
import { RootState } from '../modules/reducers'
import { useLogger } from '../providers/logger'
import { FullScreenSpinnerLoading } from '../views/components'

interface RxpEligibilityWrapperProps {
  children: React.ReactNode
  redirectOnEligible?: boolean
  skipFetch?: boolean
}

export function RxpEligibilityWrapper({ children, skipFetch = false }: RxpEligibilityWrapperProps) {
  const { isAnonymous, uid } = useAuthState()
  const firebaseInitialized = useReduxSelector((x: RootState) => x.auth.firebaseInitialized)
  const rxpRedirectAppliedRef = useRef(false)
  const isEmbed = useIsEmbed()
  const logger = useLogger('RxpEligibilityWrapper')
  const { redirectToRxp } = useRxpExperimentSetup()
  const { eligibility, setEligibility } = useRxpEligibility()

  const shouldSkipQuery = !firebaseInitialized || isAnonymous || rxpRedirectAppliedRef.current || eligibility !== null

  const { data: rxpEligibility, isLoading: rxpEligibilityLoading } = useGetRxpEligibilityQuery(
    shouldSkipQuery || skipFetch ? skipToken : { customerId: uid! },
    { refetchOnMountOrArgChange: false }
  )

  useEffect(() => {
    if (rxpEligibility?.data?.eligible !== undefined && eligibility === null) {
      setEligibility(rxpEligibility.data.eligible)
    }
  }, [rxpEligibility, eligibility, setEligibility])

  useEffect(() => {
    if (eligibility && !rxpRedirectAppliedRef.current && !isEmbed) {
      logger.info('User is eligible for RXP, redirecting from legacy to RXP')
      rxpRedirectAppliedRef.current = true
      redirectToRxp(uid!)
    }
    if (isEmbed && eligibility) {
      logger.info('User is eligible for RXP but going through embed, blocking experience')
    }
  }, [eligibility, redirectToRxp, uid, isEmbed])

  const shouldShowSpinner = rxpEligibilityLoading || (eligibility === true && !isEmbed)

  // Show loading spinner while eligibility is being fetched,
  // or if the user is eligible and should be redirected to RXP.
  // Prevents rendering any screen content prematurely.
  if (shouldShowSpinner) {
    return <FullScreenSpinnerLoading />
  }

  // No redirects or blocks, let navigation children handle user experience
  return <>{children}</>
}
